export type InvoiceFormActionId = 'save' | 'send' | 'viewPdf' | 'menu'

export type InvoiceFormMenuActionId = 'clearForm' | 'viewPdf' | 'duplicate' | 'delete'

export type ResolveInvoiceFormActionSurfacesInput = {
  isNewInvoice: boolean
  locked: boolean
  isMobile: boolean
  isInvoiceOverdue: boolean
  isZeroTotalInvoice: boolean
  isInvoiceSent: boolean
  isPreparingPdf: boolean
  hasDeleteHandler: boolean
  isGuest?: boolean
}

export type InvoiceFormActionSurfaces = {
  /** null = do not pass actions to the page header */
  header: InvoiceFormActionId[] | null
  footer: InvoiceFormActionId[]
  bottomBar: InvoiceFormActionId[]
  menuItems: InvoiceFormMenuActionId[]
  sendLabel: 'Send Invoice' | 'Send Reminder'
  saveVisible: boolean
  viewPdfLabel: 'View PDF' | 'Preparing Invoice…'
  sendDisabled: boolean
}

const PRIMARY_BAR_ACTIONS: InvoiceFormActionId[] = ['send', 'menu']

const barActions = (saveVisible: boolean): InvoiceFormActionId[] =>
  saveVisible ? ['save', ...PRIMARY_BAR_ACTIONS] : PRIMARY_BAR_ACTIONS

const desktopFooterActions = (saveVisible: boolean): InvoiceFormActionId[] => {
  const actions: InvoiceFormActionId[] = ['viewPdf']
  if (saveVisible) {
    actions.push('save')
  }
  actions.push('send')
  return actions
}

const desktopHeaderActions = (
  isNewInvoice: boolean,
  locked: boolean,
  saveVisible: boolean,
): InvoiceFormActionId[] | null => {
  if (isNewInvoice && locked) {
    return null
  }
  return barActions(saveVisible)
}

const menuItems = (
  isNewInvoice: boolean,
  hasDeleteHandler: boolean,
): InvoiceFormMenuActionId[] => {
  if (isNewInvoice) {
    return ['clearForm']
  }
  const items: InvoiceFormMenuActionId[] = ['viewPdf', 'duplicate']
  if (hasDeleteHandler) {
    items.push('delete')
  }
  return items
}

export const resolveInvoiceFormActionSurfaces = (
  input: ResolveInvoiceFormActionSurfacesInput,
): InvoiceFormActionSurfaces => {
  const saveVisible = !input.locked
  const sendLabel = input.isInvoiceOverdue ? 'Send Reminder' : 'Send Invoice'
  const viewPdfLabel = input.isPreparingPdf ? 'Preparing Invoice…' : 'View PDF'
  const sendDisabled = input.isInvoiceOverdue
    ? input.isZeroTotalInvoice
    : input.isInvoiceSent
  const items = menuItems(input.isNewInvoice, input.hasDeleteHandler)

  if (input.isGuest) {
    return {
      header: null,
      footer: [],
      bottomBar: [],
      menuItems: [],
      sendLabel,
      saveVisible: false,
      viewPdfLabel,
      sendDisabled: false,
    }
  }

  if (input.isMobile) {
    return {
      header: null,
      footer: [],
      bottomBar: barActions(saveVisible),
      menuItems: items,
      sendLabel,
      saveVisible,
      viewPdfLabel,
      sendDisabled,
    }
  }

  return {
    header: desktopHeaderActions(input.isNewInvoice, input.locked, saveVisible),
    footer: desktopFooterActions(saveVisible),
    bottomBar: [],
    menuItems: items,
    sendLabel,
    saveVisible,
    viewPdfLabel,
    sendDisabled,
  }
}
